<template>
  <div id="app-shell" :data-client-mode="clientMode">
    <ClientRouterScreen v-if="clientMode === 'router'" />

    <MobileClientScreen
      v-else-if="clientMode === 'mobile'"
      :current-view="currentView"
      :session-ready="sessionStore.sessionReady"
      :active-order-id="activeOrderId"
      :editing-order-id="editingOrderId || undefined"
      @view-change="handleViewChange"
      @order-success="handleOrderSuccess"
      @open-profile="openProfileModal"
      @open-table-selector="openTableSelector"
      @edit-order="handleEditOrder"
      @cancel-order="handleCancelOrder"
    />

    <KioskClientScreen
      v-else-if="clientMode === 'kiosk'"
      :current-view="currentView"
      :session-ready="sessionStore.sessionReady"
      @view-change="handleViewChange"
      @order-success="handleOrderSuccess"
      @open-profile="openProfileModal"
    />

    <DisplayClientScreen v-else-if="clientMode === 'display'" />

    <WebClientScreen
      v-else
      :current-view="currentView"
      :session-ready="sessionStore.sessionReady"
      :active-order-id="activeOrderId"
      :editing-order-id="editingOrderId || undefined"
      @view-change="handleViewChange"
      @order-success="handleOrderSuccess"
      @open-profile="openProfileModal"
      @open-table-selector="openTableSelector"
      @edit-order="handleEditOrder"
      @cancel-order="handleCancelOrder"
    />

    <ProfileModal
      v-if="clientMode === 'web' || clientMode === 'mobile' || clientMode === 'kiosk'"
      :is-open="isProfileModalOpen"
      :initial-tab="profileInitialTab"
      @close="closeProfileModal"
      @open-order-history="openOrderHistory"
    />

    <CartPanel
      v-if="clientMode === 'mobile'"
      :is-open="cartStore.isOpen"
      @close="closeCart"
      @checkout="proceedToCheckout"
    />

    <OrderHistoryModal ref="orderHistoryRef" />

    <TableSelectorModal
      :is-open="isTableModalOpen"
      @close="closeTableModal"
    />

    <NotificationToast />

    <ConfirmDialog
      v-if="cancelConfirm.visible"
      :model-value="cancelConfirm.visible"
      :title="cancelConfirm.title"
      :message="cancelConfirm.message"
      type="danger"
      icon="danger"
      confirm-text="Sí, cancelar"
      cancel-text="No, mantener"
      @confirm="executeCancel"
      @cancel="cancelConfirm.visible = false"
    />

    <div v-if="isLoading" class="global-loading-overlay">
      <div class="loading-spinner" />
    </div>

    <QRResolver v-if="clientMode !== 'display'" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ProfileModal from './components/profile/ProfileModal.vue';
import CartPanel from './components/cart/CartPanel.vue';
import OrderHistoryModal from './components/OrderHistory.vue';
import TableSelectorModal from './components/table/TableSelectorModal.vue';
import QRResolver from './components/core/QRResolver.vue';
import ConfirmDialog from '@shared/components/ConfirmDialog.vue';
import NotificationToast from '@shared/components/NotificationToast.vue';
import { requestJSON } from './core/http';
import { useCartStore } from './stores/cart-store';
import type { CartItem } from './modules/cart-persistence';
import { useOrdersStore } from './stores/orders-store';
import { useSessionStore } from './stores/session-store';
import { useUserStore } from './stores/user-store';
import { publishNotification } from '@shared/core/notification-center';
import {
  resolveClientContext,
  resolveClientModeFromPath,
  type ClientContextMode,
} from './context/client-context';
import ClientRouterScreen from './screens/ClientRouterScreen.vue';
import DisplayClientScreen from './screens/DisplayClientScreen.vue';
import KioskClientScreen from './screens/KioskClientScreen.vue';
import MobileClientScreen from './screens/MobileClientScreen.vue';
import WebClientScreen from './screens/WebClientScreen.vue';

const cartStore = useCartStore();
const ordersStore = useOrdersStore();
const sessionStore = useSessionStore();
const userStore = useUserStore();

type ClientShellMode = ClientContextMode | 'router';

const currentView = ref<'menu' | 'details' | 'orders'>('menu');
const activeOrderId = ref('');
const editingOrderId = ref<string | null>(null);
const cancellingOrderId = ref<string | null>(null);
const isProfileModalOpen = ref(false);
const profileInitialTab = ref<'login' | 'register'>('login');
const isLoading = ref(false);

// Cancel confirmation dialog state
const cancelConfirm = ref({
  visible: false,
  orderId: '',
  title: 'Cancelar orden',
  message: '¿Estás seguro de que deseas cancelar esta orden? Esta acción no se puede deshacer.',
});

const executeCancel = async () => {
  const { orderId } = cancelConfirm.value;
  cancelConfirm.value.visible = false;

  const success = await ordersStore.cancelOrder(orderId);
  if (success) {
    publishNotification({ message: 'Orden cancelada correctamente', type: 'success' });
    // If the active order was cancelled, navigate back to menu
    if (activeOrderId.value === orderId) {
      activeOrderId.value = '';
      currentView.value = 'menu';
    }
  } else {
    publishNotification({
      message: ordersStore.error || 'No se pudo cancelar la orden',
      type: 'error',
    });
  }
};

const clientMode = computed<ClientShellMode>(() => {
  const modeFromPath = resolveClientModeFromPath(window.location.pathname);
  if (modeFromPath) return modeFromPath;
  if (window.location.pathname.replace(/\/$/, '') === '/client') return 'router';
  return resolveClientContext(new URL(window.location.href)).mode;
});

const resolvedSessionId = computed(() => {
  return String(sessionStore.sessionId || '').trim() || null;
});

const readInitialView = (): 'menu' | 'details' | 'orders' => {
  try {
    const requestedView = String(new URLSearchParams(window.location.search).get('view') || '')
      .trim()
      .toLowerCase();
    if (requestedView === 'orders' || requestedView === 'order') return 'orders';
    if (requestedView === 'details' || requestedView === 'checkout') return 'details';
  } catch {
    // Keep safe default when URL parsing is unavailable.
  }
  return 'menu';
};

const readInitialOrderId = (): string => {
  try {
    return String(new URLSearchParams(window.location.search).get('order_id') || '').trim();
  } catch {
    return '';
  }
};

const handleViewChange = (view: 'menu' | 'details' | 'orders') => {
  if (clientMode.value === 'display') return;
  if (view === 'orders' && !ordersStore.hasActiveOrders) {
    currentView.value = 'menu';
    return;
  }
  currentView.value = view;
};

const openProfileModal = () => {
  if (
    clientMode.value !== 'web' &&
    clientMode.value !== 'mobile' &&
    clientMode.value !== 'kiosk'
  )
    return;
  profileInitialTab.value = 'login';
  isProfileModalOpen.value = true;
};

const isTableModalOpen = ref(false);

const openTableSelector = () => {
  if (currentView.value === 'details') {
    return;
  }
  isTableModalOpen.value = true;
};

const closeTableModal = () => {
  isTableModalOpen.value = false;
};

const closeProfileModal = () => {
  isProfileModalOpen.value = false;
};

const orderHistoryRef = ref<InstanceType<typeof OrderHistoryModal> | null>(null);

const openOrderHistory = () => {
  isProfileModalOpen.value = false;
  orderHistoryRef.value?.open();
};

const closeCart = () => {
  cartStore.closeCart();
};

const proceedToCheckout = () => {
  currentView.value = 'details';
  closeCart();
};

const handleOrderSuccess = (orderId: string | number) => {
  const normalizedOrderId = String(orderId || '').trim();
  if (!normalizedOrderId) return;

  if (editingOrderId.value) {
    editingOrderId.value = null;
    activeOrderId.value = normalizedOrderId;
    currentView.value = 'orders';

    const url = new URL(window.location.href);
    url.searchParams.set('view', 'orders');
    url.searchParams.set('order_id', normalizedOrderId);
    window.history.replaceState({}, '', url.toString());
    void ordersStore.fetchActiveOrders(resolvedSessionId.value || undefined).catch(() => undefined);
    return;
  }

  activeOrderId.value = normalizedOrderId;
  // The optimistic order (with items and reconciliation metadata) is registered
  // by cartStore.submitCart, the single registration point. The readback below
  // reconciles it with the canonical backend payload.
  currentView.value = 'orders';

  const url = new URL(window.location.href);
  url.searchParams.set('view', 'orders');
  url.searchParams.set('order_id', normalizedOrderId);
  window.history.replaceState({}, '', url.toString());

  // Reconcile immediately instead of waiting for the next polling tick.
  void ordersStore
    .fetchActiveOrders(resolvedSessionId.value || undefined)
    .catch(() => undefined);
};

const handleEditOrder = async (orderId: string) => {
  if (clientMode.value === 'display') return;

  const normalizedOrderId = String(orderId || '').trim();
  if (!normalizedOrderId) return;

  let order = ordersStore.getOrderById(normalizedOrderId);
  if (!order) {
    try {
      await ordersStore.fetchActiveOrders(resolvedSessionId.value || undefined);
      order = ordersStore.getOrderById(normalizedOrderId);
    } catch {
      return;
    }
  }
  if (!order || order.items.length === 0) return;

  const cartItems: CartItem[] = order.items.map((item) => ({
    id: item.menu_item_id ?? item.id,
    order_item_id: item.id,
    menu_item_id: item.menu_item_id ?? item.id,
    name: item.name,
    price: Number(item.unit_price ?? item.price ?? 0),
    quantity: Number(item.quantity || 1),
    extras: [],
    extrasTotal: 0,
    modifiers: (item.modifiers || []).map((modifier) => {
      if (typeof modifier === 'object' && modifier !== null && 'modifier_id' in modifier) {
        return (modifier as { modifier_id: string | number }).modifier_id;
      }
      return modifier;
    }),
    special_instructions: item.special_instructions || '',
  }));

  cartStore.replaceItems(cartItems);
  editingOrderId.value = normalizedOrderId;
  activeOrderId.value = normalizedOrderId;
  currentView.value = 'details';

  const url = new URL(window.location.href);
  url.searchParams.set('view', 'details');
  url.searchParams.set('order_id', normalizedOrderId);
  window.history.replaceState({}, '', url.toString());
};

const handleCancelOrder = async (orderId: string) => {
  if (!orderId) return;
  cancelConfirm.value = {
    visible: true,
    orderId,
    title: 'Cancelar orden',
    message: '¿Estás seguro de que deseas cancelar esta orden? Esta acción no se puede deshacer.',
  };
};

const bootstrapClient = async () => {
  if (clientMode.value === 'router') return;

  isLoading.value = true;
  try {
    currentView.value = clientMode.value === 'display' ? 'orders' : readInitialView();
    activeOrderId.value = readInitialOrderId();
    const params = new URLSearchParams(window.location.search);
    const requestedView = String(params.get('view') || '').trim().toLowerCase();
    if (requestedView === 'profile' && (clientMode.value === 'web' || clientMode.value === 'mobile' || clientMode.value === 'kiosk')) {
      profileInitialTab.value =
        String(params.get('tab') || '').trim().toLowerCase() === 'register'
          ? 'register'
          : 'login';
      isProfileModalOpen.value = true;
    }

    await userStore.fetchUser();
    cartStore.refreshFromPersistence();
    await sessionStore.bootstrapSessionContext();

    if (!sessionStore.sessionReady) {
      ordersStore.stopPolling();
      ordersStore.clearOrders();
    }
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Error al inicializar sesión';
    publishNotification({ message: errorMsg, type: 'error' });
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await bootstrapClient();
});

onBeforeUnmount(() => {
  ordersStore.stopPolling();
});

// #653: Use sessionStore.sessionId directly instead of resolvedSessionId to
// avoid stale closure (resolvedSessionId captured at watch creation when null).
// Track initial mount to prevent clearing persisted orders on first render.
let _hasMounted = false;
watch(
  () => [sessionStore.sessionReady, sessionStore.sessionId] as const,
  ([ready, sessionId], _prev) => {
    if (!_hasMounted) {
      _hasMounted = true;
      // Initial mount: just ensure polling is started if session exists
      if (ready && sessionId) {
        ordersStore.startPolling(sessionId);
      }
      return;
    }

    if (!ready) {
      // Session expired or invalidated (real transition): clear state
      ordersStore.stopPolling();
      ordersStore.clearOrders();
      if (currentView.value === 'orders') {
        currentView.value = 'menu';
      }
      return;
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has('qr_token')) {
      url.searchParams.delete('qr_token');
      window.history.replaceState({}, '', url.toString());
    }

    ordersStore.startPolling(sessionId || undefined);
  },
  { immediate: false },
);

watch(
  () => userStore.isAuthenticated,
  async () => {
    if (!sessionStore.sessionReady) return;
    await ordersStore.fetchActiveOrders(resolvedSessionId.value || undefined);
  },
);

watch(
  () => ordersStore.hasActiveOrders,
  (hasActiveOrders) => {
    if (!hasActiveOrders && currentView.value === 'orders' && clientMode.value !== 'display') {
      currentView.value = 'menu';
    }
  },
);
</script>

<style scoped>
#app-shell {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
}

:global(.skip-link) {
  position: absolute;
  top: -9999px;
  left: 1rem;
  z-index: 10000;
  padding: 0.6rem 1.2rem;
  background: #0f172a;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  transition: top 0.2s ease;
}

:global(.skip-link:focus),
:global(.skip-link:focus-visible) {
  top: 0;
  outline: 3px solid #ea580c;
  outline-offset: 2px;
}

#app-shell > .web-client-screen {
  flex: 1 0 auto;
}

:global(html),
:global(body),
:global(#app) {
  min-height: 100%;
  margin: 0;
}

.global-loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  width: 66px;
  height: 66px;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.25);
  border: 4px solid rgba(255, 255, 255, 0.25);
  border-top-color: #67e8f9;
  border-radius: 999px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
